import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../config/api";
import TaxonomyChips from "../../Components/TaxonomyChips/TaxonomyChips";
import useScrollToTop from "../../hooks/useScrollToTop";
import useSeo from "../../hooks/useSeo";
import { useTranslation } from "../../i18n/LanguageProvider";
import { getInitialData } from "../../utils/initialData";
import { buildDirectoryIntro, getStaticSeoForPath } from "../../utils/seo";
import "./Directory.sass";

/**
 * /{lang}/femei - every category and country that has profiles, with counts.
 */
function DirectoryPage() {
    const { language, t, localizedPath } = useTranslation();
    const [directory, setDirectory] = useState(() => getInitialData("directory"));
    const [loading, setLoading] = useState(() => !getInitialData("directory"));
    const seo = useMemo(() => getStaticSeoForPath("/femei", language), [language]);

    useScrollToTop();
    useSeo(seo);

    useEffect(() => {
        const initial = getInitialData("directory");

        if (initial) {
            setDirectory(initial);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);

        fetch(`${API.getWoman}?taxonomy=true&lang=${language}`, { credentials: "include" })
            .then((res) => res.json())
            .then((data) => {
                if (!cancelled) setDirectory(data?.data || null);
            })
            .catch(() => {
                if (!cancelled) setDirectory(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [language]);

    return (
        <main className="directory general-wrapper">
            <nav className="directory__breadcrumb">
                <Link to={localizedPath("/")}>{t("profile.home")}</Link>
                <span> / </span>
                <span>{t("directory.breadcrumb")}</span>
            </nav>
            <h1 className="directory__heading">{t("directory.title")}</h1>

            {loading && <p className="directory__loading">{t("directory.loading")}</p>}

            {!loading && directory && (
                <>
                    <p className="directory__intro">{buildDirectoryIntro(directory, language)}</p>
                    <section className="directory__section card-common" aria-labelledby="directory-categories-title">
                        <h2 id="directory-categories-title">{t("directory.categoriesTitle")}</h2>
                        <TaxonomyChips type="category" items={directory.categories} />
                    </section>
                    <section className="directory__section card-common" aria-labelledby="directory-places-title">
                        <h2 id="directory-places-title">{t("directory.placesTitle")}</h2>
                        <TaxonomyChips type="place" items={directory.places} />
                    </section>
                </>
            )}

            {!loading && !directory && <p className="directory__empty">{t("directory.empty")}</p>}
        </main>
    );
}

export default DirectoryPage;
